import { useEffect, useMemo, useState } from 'react';
import { Holding, PriceMap, Transaction } from '../types';
import { useCurrency } from '../context/CurrencyContext';
import { computeHoldings } from '../utils/calculations';
import { buildDisplayRateTable } from '../utils/displayRates';
import { UNSPECIFIED_WALLET } from '../utils/walletTimeline';
import { ALL_WALLETS, UNSPECIFIED, walletScopedTransactions } from '../utils/walletScope';

const FILTER_KEY = 'mycryptoplanner_holdings_wallet_filter';
export { ALL_WALLETS, UNSPECIFIED };

export interface WalletFilterState {
  /** ALL_WALLETS, UNSPECIFIED, or a wallet name. */
  walletFilter: string;
  setWalletFilter: (value: string) => void;
  /** Named wallets found in transactions (wallet, fromWallet, toWallet), sorted. */
  walletOptions: string[];
  /** Whether any BUY/SELL has no wallet, i.e. the "Unspecified" bucket exists. */
  hasUnspecified: boolean;
  /** Whether there is anything to filter by at all. */
  canFilter: boolean;
  /** The selected wallet's display name, or null for All Portfolios. */
  walletLabel: string | null;
  /** The selected wallet as a walletTimeline key, or null for All Portfolios. */
  timelineWallet: string | null;
  /** Holdings scoped to the selected wallet (the full holdings for All). */
  walletHoldings: Holding[];
}

/**
 * The Dashboard's wallet filter, shared by the Holdings table, the Portfolio
 * Value chart and Asset Allocation so all three always show the same wallet.
 *
 * Lifted out of HoldingsTable unchanged: same sessionStorage key, same option
 * list, same reset-to-All when the saved wallet disappears, and the same scoped
 * holdings. Whether the CHART and ALLOCATION honour it is a tier question and is
 * decided by the caller (see App.tsx), not here.
 */
export function useWalletFilter(
  holdings: Holding[],
  transactions: Transaction[],
  prices: PriceMap
): WalletFilterState {
  const [walletFilter, setWalletFilter] = useState<string>(() => {
    try { return sessionStorage.getItem(FILTER_KEY) ?? ALL_WALLETS; }
    catch { return ALL_WALLETS; }
  });

  useEffect(() => {
    try { sessionStorage.setItem(FILTER_KEY, walletFilter); } catch { /* ignore */ }
  }, [walletFilter]);

  const { currency, rate } = useCurrency();
  // One table, memoised: computeHoldings below asks for a rate per transaction
  // and reruns whenever prices or the wallet filter change.
  const displayRates = useMemo(
    () => buildDisplayRateTable(currency.code, rate),
    [currency.code, rate]
  );

  // Distinct wallet names found in transactions (wallet, fromWallet, toWallet), plus an
  // "Unspecified" bucket for BUY/SELL txs without a wallet.
  const { walletOptions, hasUnspecified } = useMemo(() => {
    const set = new Set<string>();
    let unspec = false;
    for (const tx of transactions) {
      if (tx.type === 'TRANSFER') {
        if (tx.fromWallet) set.add(tx.fromWallet);
        if (tx.toWallet)   set.add(tx.toWallet);
        continue;
      }
      const w = tx.wallet?.trim();
      if (w) set.add(w);
      else unspec = true;
    }
    return { walletOptions: Array.from(set).sort((a, b) => a.localeCompare(b)), hasUnspecified: unspec };
  }, [transactions]);

  const canFilter = walletOptions.length > 0 || hasUnspecified;

  // If the saved filter no longer exists in the wallet list, reset to All
  useEffect(() => {
    if (!canFilter) return;
    if (walletFilter === ALL_WALLETS) return;
    if (walletFilter === UNSPECIFIED && hasUnspecified) return;
    if (walletOptions.includes(walletFilter)) return;
    setWalletFilter(ALL_WALLETS);
  }, [canFilter, walletFilter, walletOptions, hasUnspecified]);

  // Re-derive holdings when a wallet filter is active so values + allocations are scoped.
  const walletHoldings = useMemo(() => {
    if (walletFilter === ALL_WALLETS) return holdings;
    // Same rate table as the unfiltered holdings, so switching the wallet filter
    // cannot change which rate a cost figure was converted at.
    return computeHoldings(walletScopedTransactions(transactions, walletFilter), prices, displayRates);
  }, [walletFilter, transactions, prices, holdings, displayRates]);

  const walletLabel =
    walletFilter === ALL_WALLETS ? null
    : walletFilter === UNSPECIFIED ? 'Unspecified'
    : walletFilter;
  const timelineWallet =
    walletFilter === ALL_WALLETS ? null
    : walletFilter === UNSPECIFIED ? UNSPECIFIED_WALLET
    : walletFilter;

  return {
    walletFilter, setWalletFilter, walletOptions, hasUnspecified, canFilter,
    walletLabel, timelineWallet, walletHoldings,
  };
}
